import type { TFunction } from "i18next";
import {
  ContinuousPerformanceStageEvent,
  CPTStageScoringResult,
  HeadVelocity,
  PupilSizeData,
  Stimulus,
  StimulusResponse,
  StimulusResponseState,
  StimulusType,
} from "types/tests";
import type { CPTIndexKey, CPTSeverity } from "./CPTAssessmentUtils";

export function renderStageDisplayName(stage: StageBucket, t: TFunction): string {
  const base = stage.displayNameKey ? t(stage.displayNameKey) : stage.displayNameFallback;
  return stage.displayOccurrence !== null ? `${base} ${stage.displayOccurrence}` : base;
}

export type StagePaletteHue = "gray" | "indigo" | "teal" | "violet";
export type StagePaletteIntensity = "light" | "dark";

export interface StagePalette {
  hue: StagePaletteHue;
  intensity: StagePaletteIntensity;
}

const STAGE_INTENSITY_REGEX = /_D(_|$)/;

export function stagePalette(stage: { role: StageRole; rawName: string }): StagePalette {
  const hue: StagePaletteHue =
    stage.role === "visualDistractor"
      ? "indigo"
      : stage.role === "auditoryDistractor"
        ? "teal"
        : stage.role === "combinedDistractor"
          ? "violet"
          : "gray";
  const intensity: StagePaletteIntensity = STAGE_INTENSITY_REGEX.test(stage.rawName)
    ? "dark"
    : "light";
  return { hue, intensity };
}

export function stagePaletteShade(palette: StagePalette): number {
  return palette.intensity === "dark" ? 7 : 3;
}

export const CPT_TIME_TICK_INTERVAL_SEC = 30;

function cptTimeLabelIntervalSec(maxT: number): number {
  if (maxT <= 1200) return 120;
  if (maxT <= 3600) return 300;
  return 600;
}

export interface CPTTimeAxis {
  domain: [number, number];
  ticks: number[];
  labels: Set<number>;
}

export function buildCPTTimeAxis(maxT: number): CPTTimeAxis {
  const safeMax = Math.max(maxT, CPT_TIME_TICK_INTERVAL_SEC);
  const domainEnd = Math.ceil(safeMax / CPT_TIME_TICK_INTERVAL_SEC) * CPT_TIME_TICK_INTERVAL_SEC;
  const ticks: number[] = [];
  for (let t = 0; t <= domainEnd; t += CPT_TIME_TICK_INTERVAL_SEC) ticks.push(t);
  const labelInterval = cptTimeLabelIntervalSec(safeMax);
  const labels = new Set<number>();
  for (let t = 0; t <= domainEnd; t += labelInterval) labels.add(t);
  return { domain: [0, domainEnd], ticks, labels };
}

export type StageRole =
  | "baseline"
  | "visualDistractor"
  | "auditoryDistractor"
  | "combinedDistractor"
  | "unknown";

export interface StageBucket {
  key: string;
  rawName: string;
  role: StageRole;
  displayNameKey: string | null;
  displayNameFallback: string;
  displayOccurrence: number | null;
  shortLabel: string;
  stageIndex: number;
  startTime: number;
  endTime: number;
  durationSec: number;
  empty: boolean;
}

export interface StageMetrics {
  stage: StageBucket;
  nStimuli: number;
  nTargets: number;
  nNonTargets: number;
  nDistractors: number;
  nResponses: number;
  nHits: number;
  nMisses: number;
  nFalseAlarms: number;
  nMultiPress: number;
  hitRate: number | null;
  timingRate: number | null;
  falseAlarmRate: number | null;
  multiPressPerTarget: number | null;
  rtMeanMs: number | null;
  rtMedianMs: number | null;
  rtSdMs: number | null;
  headVelocityMean: number | null;
  headVelocityMax: number | null;
}

export interface StageIndexCell {
  score: number | null;
  deviation: number | null;
  severity: CPTSeverity;
}

export interface StageMatrix {
  baselineKey: string | null;
  cellsByStage: Record<string, Record<CPTIndexKey, StageIndexCell>>;
}

const STAGE_DISPLAY_MAP: Record<string, { displayKey: string; short: string; role: StageRole }> = {
  ND: {
    displayKey: "extraTests.continuousPerformance.result.stage.displayName.baseline",
    short: "BL",
    role: "baseline",
  },
  VD_S: {
    displayKey: "extraTests.continuousPerformance.result.stage.displayName.visualSingle",
    short: "V·S",
    role: "visualDistractor",
  },
  VD_D: {
    displayKey: "extraTests.continuousPerformance.result.stage.displayName.visualDouble",
    short: "V·D",
    role: "visualDistractor",
  },
  AD_S: {
    displayKey: "extraTests.continuousPerformance.result.stage.displayName.auditorySingle",
    short: "A·S",
    role: "auditoryDistractor",
  },
  AD_D: {
    displayKey: "extraTests.continuousPerformance.result.stage.displayName.auditoryDouble",
    short: "A·D",
    role: "auditoryDistractor",
  },
  AVD_S: {
    displayKey: "extraTests.continuousPerformance.result.stage.displayName.combinedSingle",
    short: "C·S",
    role: "combinedDistractor",
  },
  AVD_D: {
    displayKey: "extraTests.continuousPerformance.result.stage.displayName.combinedDouble",
    short: "C·D",
    role: "combinedDistractor",
  },
};

const SUBSCRIPTS = ["₀", "₁", "₂", "₃", "₄", "₅", "₆", "₇", "₈", "₉"];

function subscript(n: number): string {
  return String(n)
    .split("")
    .map((d) => SUBSCRIPTS[Number(d)] ?? d)
    .join("");
}

function normalizeStageName(rawName: string): string {
  return rawName.replace(/_\d+$/, "");
}

export function buildStageBuckets(
  stageEvents: ContinuousPerformanceStageEvent[] | undefined,
  fallbackTestEndSec?: number,
): StageBucket[] {
  if (!stageEvents?.length) return [];

  const sorted = [...stageEvents].sort((a, b) => (a.startTime ?? 0) - (b.startTime ?? 0));
  const nameCounts = new Map<string, number>();
  const nameTotals = new Map<string, number>();

  for (const ev of sorted) {
    const key = normalizeStageName(ev.stageName);
    nameTotals.set(key, (nameTotals.get(key) ?? 0) + 1);
  }

  const buckets: StageBucket[] = [];

  for (let i = 0; i < sorted.length; i += 1) {
    const ev = sorted[i];
    const normalized = normalizeStageName(ev.stageName);
    const count = nameCounts.get(normalized) ?? 0;
    nameCounts.set(normalized, count + 1);
    const occurrence = count + 1;
    const total = nameTotals.get(normalized) ?? 1;

    const knownDisplay = STAGE_DISPLAY_MAP[normalized];
    const displayNameKey = knownDisplay ? knownDisplay.displayKey : null;
    const displayNameFallback = knownDisplay ? "" : ev.stageName;
    const shortBase = knownDisplay ? knownDisplay.short : ev.stageName;
    const role: StageRole = knownDisplay ? knownDisplay.role : "unknown";

    const displayOccurrence = total > 1 ? occurrence : null;
    const shortLabel = total > 1 ? `${shortBase}${subscript(occurrence)}` : shortBase;

    const startTime = ev.startTime ?? 0;
    const nextStart = i + 1 < sorted.length ? (sorted[i + 1].startTime ?? null) : null;
    const explicitEndRaw = (ev as { endTime?: number | null }).endTime;
    const explicitEnd =
      explicitEndRaw !== null && explicitEndRaw !== undefined && Number.isFinite(explicitEndRaw)
        ? explicitEndRaw
        : null;
    const durationFromConfig =
      typeof ev.stageDuration === "number" && Number.isFinite(ev.stageDuration)
        ? ev.stageDuration
        : null;
    const endTime =
      explicitEnd ??
      nextStart ??
      (durationFromConfig !== null ? startTime + durationFromConfig : null) ??
      fallbackTestEndSec ??
      startTime;

    buckets.push({
      key: `${ev.stageName}:${i}`,
      rawName: ev.stageName,
      role,
      displayNameKey,
      displayNameFallback,
      displayOccurrence,
      shortLabel,
      stageIndex: i,
      startTime,
      endTime,
      durationSec: Math.max(0, endTime - startTime),
      empty: endTime <= startTime,
    });
  }

  return buckets;
}

function inStage(timeStamp: number | undefined, stage: StageBucket): boolean {
  if (timeStamp === undefined || timeStamp === null || !Number.isFinite(timeStamp)) return false;
  if (stage.empty) return false;
  return timeStamp >= stage.startTime && timeStamp < stage.endTime;
}

function mean(values: number[]): number | null {
  if (!values.length) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

function median(values: number[]): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function stddev(values: number[]): number | null {
  if (values.length < 2) return null;
  const m = mean(values) as number;
  const variance = values.reduce((acc, v) => acc + (v - m) * (v - m), 0) / (values.length - 1);
  return Math.sqrt(variance);
}

const HIT_STATES = new Set<StimulusResponseState>([
  StimulusResponseState.Correct,
  StimulusResponseState.CorrectMultiPress,
]);

const FALSE_ALARM_STATES = new Set<StimulusResponseState>([
  StimulusResponseState.Incorrect,
  StimulusResponseState.IncorrectMultiPress,
  StimulusResponseState.FalsePositiveEmpty,
  StimulusResponseState.FalsePositiveAudio,
  StimulusResponseState.FalsePositiveVisual,
  StimulusResponseState.FalsePositiveAudioVisual,
]);

const MULTIPRESS_STATES = new Set<StimulusResponseState>([
  StimulusResponseState.CorrectMultiPress,
  StimulusResponseState.IncorrectMultiPress,
]);

export function computeStageMetrics(
  buckets: StageBucket[],
  stimuli: Stimulus[] | undefined | null,
  responses: StimulusResponse[] | undefined | null,
  headVelocityData: HeadVelocity[] | undefined | null,
): StageMetrics[] {
  return buckets.map((stage) => {
    const stageStimuli = (stimuli ?? []).filter((s) => inStage(s.timeStamp, stage));
    const stageResponses = (responses ?? []).filter((r) => inStage(r.timeStamp, stage));
    const stageHeadVel = (headVelocityData ?? []).filter((h) => inStage(h.timeStamp, stage));

    let nTargets = 0;
    let nNonTargets = 0;
    let nDistractors = 0;
    for (const s of stageStimuli) {
      switch (s.type) {
        case StimulusType.Target:
          nTargets += 1;
          break;
        case StimulusType.NonTarget:
          nNonTargets += 1;
          break;
        case StimulusType.VisualDistractor:
        case StimulusType.AudioDistractor:
        case StimulusType.AudioVisualDistractor:
          nDistractors += 1;
          break;
      }
    }

    let nHits = 0;
    let nMisses = 0;
    let nFalseAlarms = 0;
    let nMultiPress = 0;
    const hitRtMs: number[] = [];
    for (const r of stageResponses) {
      if (HIT_STATES.has(r.state)) {
        nHits += 1;
        if (Number.isFinite(r.responseDelta)) hitRtMs.push(r.responseDelta);
      } else if (r.state === StimulusResponseState.Miss) {
        nMisses += 1;
      } else if (FALSE_ALARM_STATES.has(r.state)) {
        nFalseAlarms += 1;
      }
      if (MULTIPRESS_STATES.has(r.state)) nMultiPress += 1;
    }

    const headVelocities = stageHeadVel
      .map((h) => h.velocity)
      .filter((v): v is number => typeof v === "number" && Number.isFinite(v));

    return {
      stage,
      nStimuli: stageStimuli.length,
      nTargets,
      nNonTargets,
      nDistractors,
      nResponses: stageResponses.length,
      nHits,
      nMisses,
      nFalseAlarms,
      nMultiPress,
      hitRate: nTargets > 0 ? nHits / nTargets : null,
      timingRate: null,
      falseAlarmRate: nNonTargets > 0 ? nFalseAlarms / nNonTargets : null,
      multiPressPerTarget: nTargets > 0 ? nMultiPress / nTargets : null,
      rtMeanMs: mean(hitRtMs),
      rtMedianMs: median(hitRtMs),
      rtSdMs: stddev(hitRtMs),
      headVelocityMean: mean(headVelocities),
      headVelocityMax: headVelocities.length ? Math.max(...headVelocities) : null,
    };
  });
}

function matchBackendStage(
  stage: StageBucket,
  backendStages: CPTStageScoringResult[],
): CPTStageScoringResult | null {
  let best: CPTStageScoringResult | null = null;
  let bestDiff = Infinity;
  for (const s of backendStages) {
    const diff = Math.abs(s.startTime - stage.startTime);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = s;
    }
  }
  return best && bestDiff <= 1 ? best : null;
}

export function resolveStageMetrics(
  buckets: StageBucket[],
  stimuli: Stimulus[] | undefined | null,
  responses: StimulusResponse[] | undefined | null,
  headVelocityData: HeadVelocity[] | undefined | null,
  backendStages?: CPTStageScoringResult[] | null,
): StageMetrics[] {
  const clientMetrics = computeStageMetrics(buckets, stimuli, responses, headVelocityData);
  if (!backendStages?.length) return clientMetrics;
  return clientMetrics.map((cm) => {
    const raw = matchBackendStage(cm.stage, backendStages)?.rawMetrics;
    if (!raw) return cm;
    return {
      ...cm,
      nTargets: raw.nTargets,
      nNonTargets: raw.nNonTargets,
      nHits: raw.hits,
      nMisses: raw.misses,
      nFalseAlarms: raw.falseAlarms,
      nMultiPress: raw.multiPressCount,
      hitRate: raw.hitRate,
      timingRate: raw.timingRate,
      falseAlarmRate: raw.falseAlarmRate,
      multiPressPerTarget: raw.nTargets > 0 ? raw.multiPressCount / raw.nTargets : null,
      rtMeanMs: raw.meanResponseTimeMs ?? cm.rtMeanMs,
      rtMedianMs: raw.medianResponseTimeMs ?? cm.rtMedianMs,
      rtSdMs: raw.sdResponseTimeMs ?? cm.rtSdMs,
    };
  });
}

export function indexScoreForStage(metrics: StageMetrics, key: CPTIndexKey): number | null {
  switch (key) {
    case "attentiveness":
      return metrics.hitRate;
    case "timeliness":
      return metrics.timingRate;
    case "impulsiveness":
      return metrics.falseAlarmRate === null ? null : 1 - metrics.falseAlarmRate;
    case "hyperReactivity":
      return metrics.multiPressPerTarget === null
        ? null
        : Math.max(0, 1 - metrics.multiPressPerTarget);
  }
}

function severityForDeviation(deviation: number | null): CPTSeverity {
  if (deviation === null || !Number.isFinite(deviation)) return "inconclusive";
  if (deviation >= -0.05) return "expected";
  if (deviation > -0.15) return "borderline";
  return "needMonitoring";
}

const ALL_INDEX_KEYS: readonly CPTIndexKey[] = [
  "attentiveness",
  "timeliness",
  "impulsiveness",
  "hyperReactivity",
] as const;

export function computeStageMatrix(metrics: StageMetrics[]): StageMatrix {
  if (!metrics.length) return { baselineKey: null, cellsByStage: {} };

  const baseline = metrics.find((m) => m.stage.role === "baseline") ?? metrics[0];
  const baselineScores: Record<CPTIndexKey, number | null> = {} as Record<
    CPTIndexKey,
    number | null
  >;
  for (const key of ALL_INDEX_KEYS) {
    baselineScores[key] = indexScoreForStage(baseline, key);
  }

  const cellsByStage: Record<string, Record<CPTIndexKey, StageIndexCell>> = {};
  for (const m of metrics) {
    const row = {} as Record<CPTIndexKey, StageIndexCell>;
    for (const key of ALL_INDEX_KEYS) {
      const score = indexScoreForStage(m, key);
      const baselineScore = baselineScores[key];
      let deviation: number | null = null;
      if (score !== null && baselineScore !== null) {
        deviation = score - baselineScore;
      }
      const isBaseline = m.stage.key === baseline.stage.key;
      let severity: CPTSeverity;
      if (score === null) {
        severity = "inconclusive";
      } else if (isBaseline) {
        severity = "expected";
      } else {
        severity = severityForDeviation(deviation);
      }
      row[key] = { score, deviation, severity };
    }
    cellsByStage[m.stage.key] = row;
  }

  return { baselineKey: baseline.stage.key, cellsByStage };
}

export interface StageDistribution {
  stage: StageBucket;
  hitRtMs: number[];
  headVelocity: number[];
  pupilSize: number[];
}

function meanPupil(sample: PupilSizeData): number | null {
  const left = typeof sample.leftPupilSize === "number" ? sample.leftPupilSize : null;
  const right = typeof sample.rightPupilSize === "number" ? sample.rightPupilSize : null;
  if (left === null && right === null) return null;
  if (left === null) return right;
  if (right === null) return left;
  return (left + right) / 2;
}

export function computeStageDistributions(
  buckets: StageBucket[],
  responses: StimulusResponse[] | undefined | null,
  headVelocityData: HeadVelocity[] | undefined | null,
  pupilSizeData: PupilSizeData[] | undefined | null,
): StageDistribution[] {
  return buckets.map((stage) => {
    const hitRtMs: number[] = [];
    for (const r of responses ?? []) {
      if (!inStage(r.timeStamp, stage)) continue;
      if (!HIT_STATES.has(r.state)) continue;
      if (Number.isFinite(r.responseDelta)) hitRtMs.push(r.responseDelta);
    }

    const headVelocity: number[] = [];
    for (const h of headVelocityData ?? []) {
      if (!inStage(h.timeStamp, stage)) continue;
      if (typeof h.velocity === "number" && Number.isFinite(h.velocity)) {
        headVelocity.push(h.velocity);
      }
    }

    const pupilSize: number[] = [];
    for (const p of pupilSizeData ?? []) {
      if (!inStage(p.timeStamp, stage)) continue;
      const m = meanPupil(p);
      if (m !== null && Number.isFinite(m)) pupilSize.push(m);
    }

    return { stage, hitRtMs, headVelocity, pupilSize };
  });
}

export interface BoxSummary {
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
  n: number;
}

function quantile(sorted: number[], q: number): number {
  if (!sorted.length) return NaN;
  const pos = (sorted.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;
  if (base + 1 < sorted.length) {
    return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
  }
  return sorted[base];
}

export function summariseBox(values: number[]): BoxSummary | null {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  return {
    n: sorted.length,
    min: sorted[0],
    q1: quantile(sorted, 0.25),
    median: quantile(sorted, 0.5),
    q3: quantile(sorted, 0.75),
    max: sorted[sorted.length - 1],
  };
}
